// src/services/audioSubmissionPipeline.js

import { getAudioBlobFromDB, updateUploadStatus, getUploadStatus } from '../../Utility/audioService';
import { uploadFileToGCS, getSignedUrl } from '../../../Backend/SupabaseApis/uploadService';
import { getCurrentUserId } from '../../../Backend/Authentication/UserStore';
import { MY_BACKEND_URL, supabase } from '../../../Backend/supabaseClient';


const UPLOAD_BATCH_SIZE = 3;
const PROCESS_BATCH_SIZE = 5;
const BACKEND_BASE_URL = MY_BACKEND_URL;

const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size));
  }
  return chunks;
};

const getMimeTypeFromBlob = (blob) => {
  return blob?.type || 'audio/webm';
};

/**
 * MAIN ENTRY POINT
 */
export const submitTestInBackground = async ({
  testId,
  questions, // [{ id, index }]
}) => {
  const userId = await getCurrentUserId();
  if (!userId) throw new Error('User not authenticated');

  // 1️⃣ Validate all audio exists
  for (const q of questions) {
    const audio = await getAudioBlobFromDB(q.id);
    if (!audio) {
      throw new Error(`Missing audio for question ${q.index}`);
    }
  }

  await Promise.all(
    questions.map((q) =>
      updateUploadStatus(q.id, q.index, testId, null, "pending")
    )
  );


  await uploadAudioBatches(testId, questions);

  await processAudioBatches({
    testId,
    questions,
    userId,
  });
};

const uploadAudioBatches = async (testId, questions) => {
  console.log("uploading started");
  const batches = chunkArray(questions, UPLOAD_BATCH_SIZE);

  for (const batch of batches) {
    await Promise.all(
      batch.map(async (q) => {
        const audioBlob = await getAudioBlobFromDB(q.id);
        if (!audioBlob) return;

        // ✅ Verify blob is real
        if (!(audioBlob instanceof Blob)) {
          throw new Error(`Audio is not a Blob for Q${q.index}`);
        }
        if (audioBlob.size < 2000) {
          throw new Error(`Audio too small for Q${q.index}: ${audioBlob.size} bytes. Try Recording again.`);
        }

        const mimeType = getMimeTypeFromBlob(audioBlob);
        const { uploadUrl, gcsUri } = await getSignedUrl(q.id, mimeType);

        // ✅ MUST await upload
        await uploadFileToGCS(uploadUrl, audioBlob, mimeType);

        await updateUploadStatus(q.id, q.index, testId, gcsUri, "pending")
      })
    );
  }
};


const processAudioBatches = async ({ testId, questions, userId }) => {
  console.log("processing started");
  const batches = chunkArray(questions, PROCESS_BATCH_SIZE);
  const { data: { session } } = await supabase.auth.getSession();

  if (!session || !session.user) {
    throw new Error("You must be logged In.");
  }

  // 2. Get the Access Token (this replaces getIdToken())
  const accessToken = session.access_token;

  for (const batch of batches) {
    await Promise.all(
      batch.map(async (q) => {
        const audioBlob = await getAudioBlobFromDB(q.id);
        if (!audioBlob) return;

        const mimeType = getMimeTypeFromBlob(audioBlob);

        const statusRecord = await getUploadStatus(q.id);
        if (!statusRecord?.uri) return;

        const res = await fetch(`${BACKEND_BASE_URL}/api/process-audio`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`, },
          body: JSON.stringify({
            uri: statusRecord.uri,
            test_id: testId,
            q_id: q.id,
            user_id: userId,
            mimeType,
            index: q.index,
          }),
        });

        if (!res.ok) throw new Error('Processing failed');
        await updateUploadStatus(q.id, q.index, testId, statusRecord.uri, 'success');
      })
    );
  }
};



// Utility/dataService.js

/**
 * Uploads a single audio blob and returns the URI.
 * Reuses your existing logic for validation and GCS signing.
 */
async function uploadSingleAudioForRetry(questionId, index) {
  // 1. Get Blob from IndexedDB
  const audioBlob = await getAudioBlobFromDB(questionId);

  if (!audioBlob) {
    throw new Error("Audio recording not found on this device. Cannot retry.");
  }

  // 2. Validate Blob
  if (!(audioBlob instanceof Blob)) {
    throw new Error(`Invalid audio data found for Q${index + 1}`);
  }
  if (audioBlob.size < 2000) {
    throw new Error(`Audio too short/empty for Q${index + 1}. Cannot retry.`);
  }

  // 3. Get Signed URL
  const mimeType = getMimeTypeFromBlob(audioBlob); // Ensure this helper is imported
  const { uploadUrl, gcsUri } = await getSignedUrl(questionId, mimeType);

  // 4. Upload to GCS
  await uploadFileToGCS(uploadUrl, audioBlob, mimeType);

  // 5. Return the URI and MimeType needed for the backend
  return { gcsUri, mimeType };
}

// ... uploadSingleAudioForRetry is fine as is ...

export async function processRetry(qAttempt, testId) { // <--- Added testId here
  const { gcsUri, mimeType } = await uploadSingleAudioForRetry(qAttempt.question_id, qAttempt.index);
  const userId = await getCurrentUserId();
  const { data: { session } } = await supabase.auth.getSession();

  if (!session || !session.user) {
    throw new Error("You must be logged In.");
  }

  const accessToken = session.access_token;

  const res = await fetch(`${BACKEND_BASE_URL}/api/retry-answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}`, },
    body: JSON.stringify({
      uri: gcsUri,
      test_id: testId, // <--- Now this is valid
      q_id: qAttempt.question_id,
      user_id: userId,
      mimeType: mimeType,
      index: qAttempt.index,
    }),
  });

  if (!res.ok) throw new Error("Retry request failed at server");

  // Optional: Return true or data if needed
  return true;
}

